import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Badge, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface ResizableSidebarProps {
  /** Whether the sidebar is open */
  isOpen: boolean;
  /** Current width of the sidebar */
  width: number;
  /** Whether the sidebar is collapsed */
  isCollapsed: boolean;
  /** Callback when width changes */
  onWidthChange: (width: number) => void;
  /** Callback when collapse state changes */
  onCollapseChange: (collapsed: boolean) => void;
  /** Content to show when expanded */
  children: React.ReactNode;
  /** Optional content to show when collapsed (e.g., badges with counts) */
  collapsedContent?: React.ReactNode;
  /** Error count to show in collapsed state */
  errorCount?: number;
  /** Warning count to show in collapsed state */
  warningCount?: number;
  /** Info count to show in collapsed state */
  infoCount?: number;
}

const COLLAPSED_WIDTH = 60;
const MIN_WIDTH = 280;
const MAX_WIDTH = 600;

export const ResizableSidebar: React.FC<ResizableSidebarProps> = ({
  isOpen,
  width,
  isCollapsed,
  onWidthChange,
  onCollapseChange,
  children,
  collapsedContent,
  errorCount = 0,
  warningCount = 0,
  infoCount = 0,
}) => {
  const theme = useTheme();
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isCollapsed) return;

      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;

      const handleMouseMove = (e: MouseEvent) => {
        const delta = startXRef.current - e.clientX; // Negative because we're on the right
        const newWidth = Math.min(Math.max(startWidthRef.current + delta, MIN_WIDTH), MAX_WIDTH);
        onWidthChange(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        cleanupRef.current = null;
      };

      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp, { passive: true });
      
      // Store cleanup function for unmount
      cleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        setIsResizing(false);
      };
    },
    [isCollapsed, width, onWidthChange],
  );

  // Clean up on unmount if still resizing
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  const toggleCollapse = useCallback(() => {
    onCollapseChange(!isCollapsed);
  }, [isCollapsed, onCollapseChange]);

  const actualWidth = isCollapsed ? COLLAPSED_WIDTH : width;
  // Calculate the displayed width based on open state
  const displayWidth = isOpen ? actualWidth : 0;

  return (
    <Box
      sx={{
        width: displayWidth,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: theme.palette.background.paper,
        borderLeft: isOpen ? `1px solid ${theme.palette.divider}` : 'none',
        transition: isResizing ? 'none' : 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        // Add transform for smooth slide-in effect
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        opacity: isOpen ? 1 : 0,
      }}
    >
      {/* Resize Handle */}
      {!isCollapsed && (
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            width: 4,
            cursor: 'col-resize',
            backgroundColor: isResizing ? theme.palette.primary.main : 'transparent',
            transition: 'background-color 150ms',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            '&:hover .resize-indicator': {
              opacity: 1,
            },
          }}
        >
          <DragIndicatorIcon
            className="resize-indicator"
            sx={{
              fontSize: 16,
              color: theme.palette.text.secondary,
              opacity: 0,
              transition: 'opacity 150ms',
              pointerEvents: 'none',
            }}
          />
        </Box>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header with Collapse Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'space-between',
            px: isCollapsed ? 0 : 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 48,
          }}
        >
          {!isCollapsed && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Validation Results
            </Typography>
          )}

          <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'} placement="left">
            <IconButton
              size="small"
              onClick={toggleCollapse}
              sx={{
                ml: isCollapsed ? 0 : 1,
              }}
            >
              {isCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: isCollapsed ? 'flex' : 'block',
            flexDirection: 'column',
            alignItems: 'center',
            pt: isCollapsed ? 2 : 0,
          }}
        >
          {isCollapsed ? (
            // Collapsed Content - Show badges
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
              }}
            >
              {errorCount > 0 && (
                <Tooltip title={`${errorCount} errors`} placement="left">
                  <Badge
                    badgeContent={errorCount}
                    color="error"
                    sx={{ cursor: 'pointer' }}
                    onClick={toggleCollapse}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.error.main,
                      }}
                    />
                  </Badge>
                </Tooltip>
              )}

              {warningCount > 0 && (
                <Tooltip title={`${warningCount} warnings`} placement="left">
                  <Badge
                    badgeContent={warningCount}
                    color="warning"
                    sx={{ cursor: 'pointer' }}
                    onClick={toggleCollapse}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.warning.main,
                      }}
                    />
                  </Badge>
                </Tooltip>
              )}

              {infoCount > 0 && (
                <Tooltip title={`${infoCount} info`} placement="left">
                  <Badge
                    badgeContent={infoCount}
                    sx={{
                      cursor: 'pointer',
                      '& .MuiBadge-badge': {
                        backgroundColor: theme.palette.info.main,
                        color: theme.palette.info.contrastText,
                      },
                    }}
                    onClick={toggleCollapse}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.info.main,
                      }}
                    />
                  </Badge>
                </Tooltip>
              )}

              {collapsedContent}
            </Box>
          ) : (
            // Expanded Content
            children
          )}
        </Box>
      </Box>
    </Box>
  );
};
